import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import { getIdToken, isAuthenticated, signOut } from '../../services/cognitoService';
import './DeleteAccount.css';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') ?? '';

const CONFIRMATION_PHRASE = 'DELETE MY ACCOUNT';
const COOLDOWN_SECONDS = 10;

type Step = 1 | 2 | 3 | 4;

/** Multi-step account deletion page with maximum friction */
const DeleteAccount: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Step tracking
  const [currentStep, setCurrentStep] = useState<Step>(1);

  // Step 1 state
  const [acknowledged, setAcknowledged] = useState(false);
  const [cooldown, setCooldown] = useState(COOLDOWN_SECONDS);
  const [cooldownActive, setCooldownActive] = useState(true);

  // Step 2 state
  const [typedPhrase, setTypedPhrase] = useState('');

  // Step 3 state
  const [selectedReason, setSelectedReason] = useState('');
  const [additionalFeedback, setAdditionalFeedback] = useState('');

  // Step 4 state
  const [verificationCode, setVerificationCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  // Shared state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auth check
  useEffect(() => {
    if (!isAuthenticated()) {
      signOut();
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  // Cooldown timer for Step 1
  useEffect(() => {
    if (!cooldownActive || cooldown <= 0) {
      setCooldownActive(false);
      return;
    }
    const timer = setTimeout(() => setCooldown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown, cooldownActive]);

  // Reset cooldown when step changes away from 1 and back
  useEffect(() => {
    if (currentStep === 1) {
      setCooldown(COOLDOWN_SECONDS);
      setCooldownActive(true);
    }
  }, [currentStep]);

  /** Step 1 → Step 2 */
  const handleStep1Continue = useCallback(() => {
    if (!acknowledged || cooldownActive) return;
    setError('');
    setCurrentStep(2);
  }, [acknowledged, cooldownActive]);

  /** Step 2 → Step 3 */
  const handleStep2Continue = useCallback(() => {
    if (typedPhrase !== CONFIRMATION_PHRASE) {
      setError(
        t(
          'settings.deleteAccount.phraseMismatch',
          'The text you entered does not match. Please try again.',
        ),
      );
      return;
    }
    setError('');
    setCurrentStep(3);
  }, [typedPhrase, t]);

  /** Step 3 → Step 4 */
  const handleStep3Continue = useCallback(() => {
    if (!selectedReason) {
      setError(t('settings.deleteAccount.reasonRequired', 'Please select a reason to continue.'));
      return;
    }
    setError('');
    setCurrentStep(4);
  }, [selectedReason, t]);

  /** Request deletion code (Step 4) */
  const handleRequestCode = async () => {
    setLoading(true);
    setError('');

    try {
      const token = getIdToken();
      if (!token) {
        setError(t('settings.deleteAccount.notLoggedIn', 'Please log in again'));
        return;
      }

      const response = await fetch(`${API_BASE_URL}/user/account/delete-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error ?? 'Failed to send verification code');
      }

      setMaskedEmail(data.email_masked ?? '');
      setCodeSent(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('settings.deleteAccount.requestFailed', 'Failed to request deletion'),
      );
    } finally {
      setLoading(false);
    }
  };

  /** Confirm deletion with code (Step 4) */
  const handleConfirmDelete = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError(t('settings.deleteAccount.invalidCode', 'Please enter a valid 6-digit code'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = getIdToken();
      if (!token) {
        setError(t('settings.deleteAccount.notLoggedIn', 'Please log in again'));
        return;
      }

      const response = await fetch(`${API_BASE_URL}/user/account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          confirmation_code: verificationCode,
          reason: selectedReason,
          feedback: additionalFeedback || undefined,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error ?? 'Failed to delete account');
      }

      signOut();
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('settings.deleteAccount.deleteFailed', 'Failed to delete account'),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setError('');
    if (currentStep === 1) {
      navigate('/settings');
    } else {
      setCurrentStep((prev) => (prev - 1) as Step);
    }
  };

  const phraseMatches = typedPhrase === CONFIRMATION_PHRASE;

  const reasons = [
    {
      key: 'not_using',
      label: t('settings.deleteAccount.reasons.notUsing', "I'm not using TheyChat anymore"),
    },
    { key: 'privacy', label: t('settings.deleteAccount.reasons.privacy', 'Privacy concerns') },
    {
      key: 'alternative',
      label: t('settings.deleteAccount.reasons.alternative', 'Found a better alternative'),
    },
    {
      key: 'too_expensive',
      label: t('settings.deleteAccount.reasons.tooExpensive', 'Too expensive'),
    },
    {
      key: 'not_satisfied',
      label: t('settings.deleteAccount.reasons.notSatisfied', 'Not satisfied with the service'),
    },
    { key: 'other', label: t('settings.deleteAccount.reasons.other', 'Other reason') },
  ];

  const stepLabels = [
    t('settings.deleteAccount.step1Label', 'Understand'),
    t('settings.deleteAccount.step2Label', 'Confirm'),
    t('settings.deleteAccount.step3Label', 'Reason'),
    t('settings.deleteAccount.step4Label', 'Verify'),
  ];

  /** Render the progress stepper */
  const renderStepper = () => (
    <div className="delete-stepper">
      {[1, 2, 3, 4].map((step, index) => (
        <div
          key={step}
          style={{ display: 'flex', alignItems: 'center', flex: index < 3 ? 1 : 'unset' }}
        >
          <div className="delete-stepper-step">
            <div
              className={`delete-stepper-circle ${
                step < currentStep ? 'completed' : step === currentStep ? 'active' : 'inactive'
              }`}
            >
              {step < currentStep ? (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                >
                  <title>Completed</title>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                step
              )}
            </div>
            <span
              className={`delete-stepper-label ${
                step < currentStep ? 'completed' : step === currentStep ? 'active' : ''
              }`}
            >
              {stepLabels[index]}
            </span>
          </div>
          {index < 3 && (
            <div className={`delete-stepper-line ${step < currentStep ? 'completed' : ''}`} />
          )}
        </div>
      ))}
    </div>
  );

  /** Step 1 — Consequences */
  const renderStep1 = () => (
    <>
      <h2 className="delete-step-title">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <title>Warning</title>
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        {t('settings.deleteAccount.step1Title', "What you'll lose")}
      </h2>
      <p className="delete-step-description">
        {t(
          'settings.deleteAccount.step1Description',
          'Deleting your account is permanent and irreversible. Please carefully read what will be lost:',
        )}
      </p>

      <ul className="delete-consequences-list">
        {[
          t(
            'settings.deleteAccount.consequence1',
            'Your profile, display name, and all personal information will be permanently erased.',
          ),
          t(
            'settings.deleteAccount.consequence2',
            'All your chat conversations and message history will be deleted and cannot be recovered.',
          ),
          t(
            'settings.deleteAccount.consequence3',
            'Your active subscription will be cancelled. No refunds will be provided for remaining time.',
          ),
          t(
            'settings.deleteAccount.consequence4',
            'All credits — subscription credits and purchased add-on credits — will be forfeited.',
          ),
          t(
            'settings.deleteAccount.consequence5',
            "Any sponsorships you've made will remain, but your account association will be removed.",
          ),
        ].map((consequence) => (
          <li key={consequence} className="delete-consequence-item">
            <svg
              className="delete-consequence-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <title>Warning</title>
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <span className="delete-consequence-text">{consequence}</span>
          </li>
        ))}
      </ul>

      <label className={`delete-acknowledge-wrapper ${acknowledged ? 'checked' : ''}`}>
        <input
          type="checkbox"
          className="delete-checkbox-hidden"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
        />
        <div className={`delete-acknowledge-checkbox ${acknowledged ? 'checked' : ''}`}>
          {acknowledged && (
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="3"
            >
              <title>Checked</title>
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>
        <span className="delete-acknowledge-label">
          {t(
            'settings.deleteAccount.acknowledgeLabel',
            'I understand that deleting my account is permanent and all my data, conversations, credits, and subscription will be irreversibly lost.',
          )}
        </span>
      </label>

      {cooldownActive && (
        <div className="delete-cooldown">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <title>Timer</title>
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span>
            {t('settings.deleteAccount.cooldownText', 'Please read carefully before continuing.')}{' '}
            <span className="delete-cooldown-timer">{cooldown}s</span>
          </span>
        </div>
      )}

      {error && (
        <div className="delete-error-message">
          <span>{error}</span>
        </div>
      )}

      <div className="delete-actions">
        <button type="button" className="delete-btn-back" onClick={handleBack}>
          {t('settings.deleteAccount.backToSettings', 'Back to Settings')}
        </button>
        <button
          type="button"
          className="delete-btn-continue"
          disabled={!acknowledged || cooldownActive}
          onClick={handleStep1Continue}
        >
          {t('common.continue', 'Continue')}
        </button>
      </div>
    </>
  );

  /** Step 2 — Type confirmation */
  const renderStep2 = () => (
    <>
      <h2 className="delete-step-title">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <title>Keyboard</title>
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <line x1="6" y1="8" x2="6.01" y2="8" />
          <line x1="10" y1="8" x2="10.01" y2="8" />
          <line x1="14" y1="8" x2="14.01" y2="8" />
          <line x1="18" y1="8" x2="18.01" y2="8" />
          <line x1="6" y1="12" x2="6.01" y2="12" />
          <line x1="10" y1="12" x2="10.01" y2="12" />
          <line x1="14" y1="12" x2="14.01" y2="12" />
          <line x1="18" y1="12" x2="18.01" y2="12" />
          <line x1="8" y1="16" x2="16" y2="16" />
        </svg>
        {t('settings.deleteAccount.step2Title', 'Confirm your intent')}
      </h2>
      <p className="delete-step-description">
        {t(
          'settings.deleteAccount.step2Description',
          'To confirm you want to permanently delete your account, please type the following phrase exactly as shown:',
        )}
      </p>

      <div className="delete-type-confirm-box">
        <div className="delete-type-confirm-phrase">
          <code>{CONFIRMATION_PHRASE}</code>
        </div>

        <input
          type="text"
          className={`delete-type-input ${phraseMatches ? 'matched' : ''}`}
          value={typedPhrase}
          onChange={(e) => {
            setTypedPhrase(e.target.value);
            setError('');
          }}
          placeholder={t('settings.deleteAccount.typePhraseHint', 'Type the phrase above...')}
          id="delete-confirm-phrase-input"
          autoComplete="off"
          spellCheck={false}
        />

        {typedPhrase.length > 0 && (
          <div
            className={`delete-type-match-indicator ${phraseMatches ? 'matched' : 'mismatched'}`}
          >
            {phraseMatches ? (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <title>Match</title>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span>{t('settings.deleteAccount.phraseMatched', 'Phrase matches')}</span>
              </>
            ) : (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <title>Mismatch</title>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                <span>{t('settings.deleteAccount.phraseNotMatched', 'Phrase does not match')}</span>
              </>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="delete-error-message">
          <span>{error}</span>
        </div>
      )}

      <div className="delete-actions">
        <button type="button" className="delete-btn-back" onClick={handleBack}>
          {t('common.back', 'Back')}
        </button>
        <button
          type="button"
          className="delete-btn-continue"
          disabled={!phraseMatches}
          onClick={handleStep2Continue}
        >
          {t('common.continue', 'Continue')}
        </button>
      </div>
    </>
  );

  /** Step 3 — Reason */
  const renderStep3 = () => (
    <>
      <h2 className="delete-step-title">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <title>Reason</title>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        {t('settings.deleteAccount.step3Title', "Tell us why you're leaving")}
      </h2>
      <p className="delete-step-description">
        {t(
          'settings.deleteAccount.step3Description',
          "We'd appreciate understanding why you're deleting your account. This helps us improve TheyChat for everyone.",
        )}
      </p>

      <div
        className="delete-reasons-grid"
        role="radiogroup"
        aria-label={t('settings.deleteAccount.step3Title', 'Reason for leaving')}
      >
        {reasons.map((reason) => (
          <label
            key={reason.key}
            className={`delete-reason-option ${selectedReason === reason.key ? 'selected' : ''}`}
          >
            <input
              type="radio"
              name="delete-reason"
              value={reason.key}
              checked={selectedReason === reason.key}
              onChange={() => {
                setSelectedReason(reason.key);
                setError('');
              }}
              className="delete-checkbox-hidden"
            />
            <div
              className={`delete-reason-radio ${selectedReason === reason.key ? 'selected' : ''}`}
            >
              <div className="delete-reason-radio-dot" />
            </div>
            <span className="delete-reason-label">{reason.label}</span>
          </label>
        ))}
      </div>

      <textarea
        className="delete-feedback-textarea"
        value={additionalFeedback}
        onChange={(e) => setAdditionalFeedback(e.target.value)}
        placeholder={t(
          'settings.deleteAccount.feedbackPlaceholder',
          'Any additional feedback? (optional)',
        )}
        maxLength={500}
      />

      {error && (
        <div className="delete-error-message">
          <span>{error}</span>
        </div>
      )}

      <div className="delete-actions">
        <button type="button" className="delete-btn-back" onClick={handleBack}>
          {t('common.back', 'Back')}
        </button>
        <button
          type="button"
          className="delete-btn-continue"
          disabled={!selectedReason}
          onClick={handleStep3Continue}
        >
          {t('common.continue', 'Continue')}
        </button>
      </div>
    </>
  );

  /** Step 4 — Verification code */
  const renderStep4 = () => (
    <>
      <h2 className="delete-step-title">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <title>Email</title>
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
        {t('settings.deleteAccount.step4Title', 'Final verification')}
      </h2>
      <p className="delete-step-description">
        {codeSent
          ? t(
              'settings.deleteAccount.step4DescriptionSent',
              'A 6-digit verification code has been sent to your email. Enter it below to permanently delete your account.',
            )
          : t(
              'settings.deleteAccount.step4Description',
              "As a final step, we'll send a verification code to your registered email. This ensures only you can delete this account.",
            )}
      </p>

      {!codeSent ? (
        <>
          {error && (
            <div className="delete-error-message">
              <span>{error}</span>
            </div>
          )}
          <div className="delete-actions">
            <button type="button" className="delete-btn-back" onClick={handleBack}>
              {t('common.back', 'Back')}
            </button>
            <button
              type="button"
              className="delete-btn-send-code"
              onClick={handleRequestCode}
              disabled={loading}
            >
              {loading && <span className="delete-spinner" />}
              {t('settings.deleteAccount.sendCode', 'Send code')}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="delete-verify-email-info">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <title>Email</title>
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            <span>
              {t('settings.deleteAccount.codeSent', 'We sent a 6-digit code to')}{' '}
              <strong>{maskedEmail}</strong>
            </span>
          </div>

          <div className="delete-verify-code-input">
            <input
              type="text"
              maxLength={6}
              value={verificationCode}
              onChange={(e) => {
                setVerificationCode(e.target.value.replace(/\D/g, ''));
                setError('');
              }}
              placeholder="000000"
              id="delete-verification-code"
            />
          </div>

          <p className="delete-verify-hint">
            {t('settings.deleteAccount.codeHint', 'Enter the 6-digit code from your email')}
          </p>

          {error && (
            <div className="delete-error-message">
              <span>{error}</span>
            </div>
          )}

          <div className="delete-actions">
            <button type="button" className="delete-btn-back" onClick={handleBack}>
              {t('common.back', 'Back')}
            </button>
            <button
              type="button"
              className="delete-btn-final-delete"
              onClick={handleConfirmDelete}
              disabled={loading || verificationCode.length !== 6}
            >
              {loading && <span className="delete-spinner" />}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <title>Delete</title>
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              {t('settings.deleteAccount.finalDeleteButton', 'Permanently Delete Account')}
            </button>
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="delete-account-page-wrapper">
      <div className="delete-account-bg" />

      <div className="delete-account-page">
        <Header isLoggedIn={isAuthenticated()} />

        <main className="delete-account-main">
          <div className="delete-account-container">
            {/* Back link */}
            <button
              type="button"
              className="delete-account-back-link"
              onClick={() => navigate('/settings')}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <title>Back</title>
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              {t('settings.deleteAccount.backToSettings', 'Back to Settings')}
            </button>

            {/* Page Title */}
            <div className="delete-account-header">
              <div className="delete-account-title-wrapper">
                <span className="delete-account-title-line" />
                <h1 className="delete-account-title">
                  {t('settings.deleteAccount.pageTitle', 'Delete Account')}
                </h1>
                <span className="delete-account-title-line" />
              </div>
              <p className="delete-account-subtitle">
                {t(
                  'settings.deleteAccount.pageSubtitle',
                  'This action is permanent and cannot be undone',
                )}
              </p>
            </div>

            {/* Stepper */}
            {renderStepper()}

            {/* Content Card */}
            <div className="delete-card" key={currentStep}>
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default DeleteAccount;
